import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Sparkles, X, ArrowRight } from "lucide-react";

const DISMISS_KEY = "high95_drop_dismissed_at";

export default function DropBanner() {
    const [ann, setAnn] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/announcement");
                if (cancelled) return;
                if (data?.active && data?.title) {
                    // Re-show banner if updated_at is newer than the last dismiss timestamp
                    const last = localStorage.getItem(DISMISS_KEY);
                    const updated = new Date(data.updated_at).getTime();
                    if (last && Number(last) >= updated) {
                        setDismissed(true);
                    }
                    setAnn(data);
                }
            } catch (e) {
                /* silent */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (!ann || !ann.active || !ann.title || dismissed) return null;

    const dismiss = () => {
        localStorage.setItem(
            DISMISS_KEY,
            String(new Date(ann.updated_at).getTime())
        );
        setDismissed(true);
    };

    return (
        <section
            data-testid="drop-banner"
            className="relative z-30 border-y border-highway-red/30 bg-gradient-to-r from-highway-red/15 via-highway-red/5 to-transparent backdrop-blur-md"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="shrink-0 w-9 h-9 rounded-full bg-highway-red/15 border border-highway-red/40 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-highway-red" />
                    </div>
                    <div className="min-w-0">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-highway-red leading-none">
                            // Today's Drop
                        </p>
                        <p
                            data-testid="drop-banner-title"
                            className="font-display font-semibold text-white text-sm sm:text-base leading-tight mt-1"
                        >
                            {ann.title}
                            {ann.body && (
                                <span className="font-sans font-normal text-white/70 ml-2">
                                    {ann.body}
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                    {ann.cta_text && ann.cta_url && (
                        <a
                            data-testid="drop-banner-cta"
                            href={ann.cta_url}
                            target={
                                ann.cta_url.startsWith("http")
                                    ? "_blank"
                                    : undefined
                            }
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-white text-black text-xs font-semibold hover:bg-white/90 transition"
                        >
                            {ann.cta_text}
                            <ArrowRight className="w-3 h-3" />
                        </a>
                    )}
                    <button
                        data-testid="drop-banner-dismiss"
                        onClick={dismiss}
                        aria-label="Dismiss"
                        className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </section>
    );
}
